import React from 'react';
import { Wallet, TrendingUp, Users, Shield } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { sectorDetailData } from '../../../data/sectorData';

const getPlayerSignalStyle = (signal: string): string => {
  const styles: Record<string, string> = {
    'LEADER': 'bg-data-green/20 text-data-green border-data-green/30',
    'STRONG': 'bg-data-blue/20 text-data-blue border-data-blue/30',
    'GROWING': 'bg-data-amber/20 text-data-amber border-data-amber/30',
    'EMERGING': 'bg-gray-600/20 text-gray-400 border-gray-500/30',
  };
  return styles[signal] || 'bg-gray-600/20 text-gray-400 border-gray-500/30';
};

interface FintechPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const FintechPanel: React.FC<FintechPanelProps> = ({ isMinimized, onMinimize }) => {
  const { fintech } = sectorDetailData;
  const { market, subsectors, keyPlayers, gaps, regulations } = fintech;

  return (
    <Panel title="FINTECH" subtitle="STRONG BUY" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero - Score & TAM */}
        <div className="text-center py-4 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Wallet size={16} className="text-data-green" />
            <span className="text-xs text-data-green uppercase tracking-widest font-semibold">
              Score {fintech.score}/100
            </span>
          </div>
          <div className="text-4xl font-mono font-black text-data-amber mb-1">
            ${market.tam2028}B
          </div>
          <div className="text-xs text-gray-500">TAM 2028</div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <TrendingUp size={14} className="text-data-green" />
            <span className="text-sm font-semibold text-data-green">+{market.cagr}% CAGR</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-xl font-mono font-bold text-white">{market.penetration}%</div>
            <div className="text-xs text-gray-500">Penetration</div>
            <div className="h-1.5 bg-terminal-border rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-data-green rounded-full" style={{ width: `${(market.penetration / market.targetPenetration) * 100}%` }} />
            </div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-xl font-mono font-bold text-data-amber">${market.tam2024}B</div>
            <div className="text-xs text-gray-500">Current TAM</div>
          </div>
        </div>

        {/* Key Players */}
        <div className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30 mb-4">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Key Players</div>
          <div className="space-y-2">
            {keyPlayers.slice(0, 3).map((p) => (
              <div key={p.name} className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{p.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400">${p.valuation}M</span>
                  <span className={`text-xs px-2 py-1 rounded border ${getPlayerSignalStyle(p.signal)}`}>
                    {p.signal}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Regulation Status */}
        <div className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Regulation</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Open Banking</span>
              <span className="text-data-green font-semibold">{regulations.openBanking}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Sandbox</span>
              <span className="text-data-green font-semibold">{regulations.sandbox}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Digi License</span>
              <span className="text-data-green font-semibold">{regulations.digitalBankLicense}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Foreign Own</span>
              <span className="text-white font-semibold">{regulations.foreignOwnership}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default FintechPanel;
